import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { legalVaultService } from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiEdit3, FiCheckCircle, FiArchive, FiAlertCircle, FiArrowRight, FiLink, FiClock } = FiIcons;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [publicLinks, setPublicLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const navigate = useNavigate();

  const canEditDocuments = legalVaultService.hasAdminAccess();
  const canManageSettings = legalVaultService.hasSettingsAccess();

  useEffect(() => {
    let cancelled = false;
    let requestSequence = 0;

    const loadDashboard = async (showLoading = false) => {
      const requestId = ++requestSequence;
      if (showLoading) setLoading(true);

      const requests = [
        legalVaultService.getDocuments(),
        canManageSettings ? legalVaultService.getPublicLinks() : Promise.resolve([]),
      ];
      const [documentsResult, linksResult] = await Promise.allSettled(requests);
      if (cancelled || requestId !== requestSequence) return;

      const docs = documentsResult.status === 'fulfilled' && Array.isArray(documentsResult.value)
        ? documentsResult.value
        : [];
      const links = linksResult.status === 'fulfilled' && Array.isArray(linksResult.value)
        ? linksResult.value
        : [];

      setDocuments(docs);
      setPublicLinks(links);
      setStats({
        total: docs.length,
        draft: docs.filter((doc) => doc.status === 'Draft').length,
        published: docs.filter((doc) => doc.status === 'Published').length,
        archived: docs.filter((doc) => doc.status === 'Archived').length,
        dueForReview: docs.filter((doc) => Boolean(legalVaultService.getReviewStatus?.(doc.reviewDate))).length,
      });
      setLastUpdatedAt(new Date());
      setLoading(false);
    };

    const refreshIfVisible = () => {
      if (window.document.visibilityState === 'visible') loadDashboard(false);
    };

    loadDashboard(true);
    const intervalId = window.setInterval(refreshIfVisible, 30000);
    window.addEventListener('focus', refreshIfVisible);
    window.document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      window.document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [canManageSettings]);

  const handleCardClick = (filterType) => {
    navigate(`/documents?filter=${encodeURIComponent(filterType)}`);
  };

  const statCards = [
    { title: 'Total Documents', value: stats?.total, icon: FiFileText, color: 'bg-blue-500', filter: 'all' },
    { title: 'Draft Documents', value: stats?.draft, icon: FiEdit3, color: 'bg-slate-500', filter: 'Draft' },
    { title: 'Published Documents', value: stats?.published, icon: FiCheckCircle, color: 'bg-emerald-500', filter: 'Published' },
    { title: 'Archived Documents', value: stats?.archived, icon: FiArchive, color: 'bg-gray-500', filter: 'Archived' },
    { title: 'Due For Review', value: stats?.dueForReview, icon: FiAlertCircle, color: 'bg-red-500', filter: 'review' },
  ];

  const attentionItems = useMemo(() => {
    const items = [];
    const now = Date.now();
    const activeLinkByDocument = new Map();

    publicLinks.forEach((link) => {
      if (link?.documentId && link.isActive) activeLinkByDocument.set(link.documentId, link);
    });

    documents.forEach((doc) => {
      const reviewStatus = legalVaultService.getReviewStatus?.(doc.reviewDate);
      if (reviewStatus && ['Overdue', 'Due today', 'Due soon'].includes(reviewStatus.status)) {
        items.push({
          key: `review-${doc.id}`,
          priority: reviewStatus.status === 'Overdue' ? 1 : 2,
          icon: FiClock,
          title: doc.title,
          message: reviewStatus.status === 'Overdue' ? 'Legal review is overdue.' : `Legal review is ${reviewStatus.status.toLowerCase()}.`,
          actionLabel: 'Review',
          onAction: () => navigate(`/documents/${doc.id}`),
        });
      }

      if (doc.status === 'Draft') {
        const updatedAt = new Date(doc.updated || doc.updatedAt || doc.created || doc.createdAt || 0).getTime();
        const ageDays = Number.isFinite(updatedAt) && updatedAt > 0 ? Math.floor((now - updatedAt) / 86400000) : 0;
        if (ageDays >= 30) {
          items.push({
            key: `draft-${doc.id}`,
            priority: 3,
            icon: FiEdit3,
            title: doc.title,
            message: `Draft has not been updated for ${ageDays} days.`,
            actionLabel: 'Open Drafts',
            onAction: () => navigate('/documents?filter=Draft'),
          });
        }
      }

      if (
        canManageSettings &&
        doc.status === 'Published' &&
        (doc.visibility === 'Public' || doc.visibility === 'Unlisted') &&
        !activeLinkByDocument.has(doc.id)
      ) {
        items.push({
          key: `link-${doc.id}`,
          priority: 1,
          icon: FiLink,
          title: doc.title,
          message: 'Published document does not currently have an active public link.',
          actionLabel: 'Manage Links',
          onAction: () => navigate('/public-links'),
        });
      }
    });

    return items.sort((a, b) => a.priority - b.priority).slice(0, 6);
  }, [documents, publicLinks, canManageSettings, navigate]);

  if (loading || !stats) return <div className="p-8 text-center text-slate-500">Loading Dashboard...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Welcome to BPA Legal Vault</h2>
          <p className="text-slate-500 mt-1">Overview of your corporate legal documents and policies.</p>
        </div>
        <div className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700" title="Dashboard refreshes automatically every 30 seconds and when you return to this tab.">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Live data{lastUpdatedAt ? ` · ${lastUpdatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card) => (
          <button
            type="button"
            key={card.title}
            onClick={() => handleCardClick(card.filter)}
            aria-label={`${card.title}: ${card.value}. Open filtered documents.`}
            className="text-left bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">{card.title}</p>
                <h3 className="text-3xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{card.value}</h3>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white ${card.color} shadow-inner`}>
                <SafeIcon icon={card.icon} className="text-xl" />
              </div>
            </div>
            <div className="mt-4 inline-flex items-center text-xs font-semibold text-slate-400 group-hover:text-blue-600 transition-colors">
              View documents <SafeIcon icon={FiArrowRight} className="ml-1.5" />
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Needs Attention</h3>
            <p className="text-sm text-slate-500 mt-0.5">Operational items that may need review or follow-up.</p>
          </div>
          {attentionItems.length > 0 && (
            <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">{attentionItems.length} shown</span>
          )}
        </div>
        {attentionItems.length === 0 ? (
          <div className="p-6 flex items-center text-sm text-emerald-700 bg-emerald-50/40">
            <SafeIcon icon={FiCheckCircle} className="mr-2 text-lg" />
            No immediate review, stale draft or public-link issues detected.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {attentionItems.map((item) => (
              <div key={item.key} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={item.icon} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.title}</p>
                    <p className="text-sm text-slate-500">{item.message}</p>
                  </div>
                </div>
                <button type="button" onClick={item.onAction} className="inline-flex items-center justify-center px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-white hover:border-blue-300 hover:text-blue-700 transition-colors flex-shrink-0">
                  {item.actionLabel}
                  <SafeIcon icon={FiArrowRight} className="ml-1.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          {canEditDocuments && (
            <button onClick={() => navigate('/documents/new/edit')} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors">Create New Document</button>
          )}
          {canEditDocuments && (
            <button onClick={() => navigate('/templates')} className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">Browse Templates</button>
          )}
          <button onClick={() => navigate('/documents?filter=review')} className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">Review Centre</button>
          {canManageSettings && (
            <button onClick={() => navigate('/public-links')} className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">Manage Public Links</button>
          )}
          {canManageSettings && (
            <button onClick={() => navigate('/settings')} className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">Manage Branding</button>
          )}
        </div>
      </div>
    </motion.div>
  );
}